import React from "react";
import {
    Table,
    Grid,
    Icon,
    Button,
    Divider,
    Popup,
    Checkbox
} from "semantic-ui-react";
import PaginationComponent from "../../components/Pagination";
import {objectComparer, sortingDirections} from "../../util/sortUtil";

import {
    IColumnMetadata,
    ICustomColumnsTransformation,
    IDataTableData
} from "../../features/application/api/applicationModels";
import DataTableHeader from "./DataTableHeader";
import DataTableBody from "./DataTableBody";
import NoResultsTableBody from "./NoResultsTableBody";
import {isArray, isEqual} from "lodash";

import {ComparisonMethodType} from "@/features/monitors/api/monitorsModels";
import {
    IDataTableFilter,
    IDataTableFilterMap,
    INumericalFilter
} from "./DataTableModels";
import {ModuleType} from "@/features/configuration/api/uiConfigurationModels";
import ExportExcel from "@/components/ExportExcel";
import {IsMobileScreen} from "@/util/appParamsUtil";
import DataTableFooter from "./DataTableFooter";
import dataFormatUtil, {DataType} from "@/util/dataFormatUtil";

interface IProps {
    staticColumns: IColumnMetadata[];
    configurableColumns?: IColumnMetadata[];
    data: IDataTableData[];
    tableFooterData?: IDataTableData[];
    sortColumnKey: string | string[]; //
    itemsPerPage: number;
    customColumns: ICustomColumnsTransformation;
    message?: JSX.Element;
    sortDirection?: sortingDirections; // Starting sort direction for the sortColumnKey column when the table is loaded
    hideNoResultsMessage?: boolean;

    // Filters can be controlled from a parent component. This allows the filters to be reused for several DataTable components
    //key is the metricCode, value is a Set values to use as the filter
    filters?: Map<string, IDataTableFilter>;
    setFilters?(
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ): void;
    removeAllFilters?(): void;
    moduleType?: ModuleType;
    exportFileName?: string;
    screenWidth: number;
    retainActivePage?: boolean;
    hasTableToggle?: boolean;
    toggleColumnKeys?: string;
    defaultSortDirection?: sortingDirections; // When a column is clicked to sort, this is the direction it will sort in first
    renderDetailBody?:
        | ((data: IDataTableData, isMobileScreen: boolean) => JSX.Element)
        | undefined
        | null;
}
interface IState {
    activePage: number;
    currentIndex: number;
    sortColumnKey: string | string[];
    sortDirection: sortingDirections;

    // Filters can either be controlled from a parent component or if not then the DataTable controls them itself
    // if hasFilter flag is set to true
    filters: Map<string, IDataTableFilter>;
    setFilters(
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ): void;
    removeAllFilters(): void;
    isToggleChecked: boolean;
}

export class DataTable extends React.Component<IProps, IState> {
    static defaultProps = {
        sortColumnKey: "",
        itemsPerPage: 100,

        customColumns: {
            "": {
                getCustomColumnMetaData: (): JSX.Element => {
                    return <></>;
                }
            }
        },
        hasTableToggle: false
    };

    constructor(props: IProps) {
        super(props);
        this.state = {
            sortColumnKey: this.props.sortColumnKey,
            sortDirection: this.props.sortDirection
                ? this.props.sortDirection
                : sortingDirections.Ascending,
            currentIndex: 0,
            activePage: 1,
            filters: new Map<string, IDataTableFilter>(),
            setFilters: (
                filterColumnMetricCode: string,
                selectedValues: Set<string | boolean | number | undefined | null>
            ): void => {
                //create a new object based on the values from state
                const filters: IDataTableFilterMap = new Map(this.state.filters);
                if (selectedValues.size > 0) {
                    filters.set(filterColumnMetricCode, selectedValues);
                } else {
                    //remove the filter if all values were cleared
                    filters.delete(filterColumnMetricCode);
                }

                this.setState({
                    filters: filters
                });
            },
            removeAllFilters: (): void => {
                this.setState({
                    filters: new Map<string, IDataTableFilter>()
                });
            },
            isToggleChecked: false
        };
        this.handleSort = this.handleSort.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.moveToFirstPage = this.moveToFirstPage.bind(this);
    }

    // When data or filters change then move to the first page
    componentDidUpdate(prevProps: IProps, prevState: IState): void {
        if (
            !this.props.retainActivePage &&
            (!isEqual(prevProps.data, this.props.data) ||
                !isEqual(prevState.filters, this.state.filters))
        ) {
            this.moveToFirstPage();
        }
    }

    handlePageChange(
        _e: React.MouseEvent<HTMLAnchorElement>,
        {activePage}: {activePage?: number | string}
    ): void {
        this.setState({
            activePage: activePage as number,
            currentIndex: ((activePage as number) - 1) * this.props.itemsPerPage
        });
    }

    // used to move to Page 1
    moveToFirstPage(): void {
        this.setState({
            activePage: 1,
            currentIndex: 0
        });
    }
    handleSort(clickedColumn: string): void {
        const {sortColumnKey, sortDirection} = this.state;

        if (sortColumnKey === clickedColumn) {
            // toggling column from sorting one direction to the other
            sortDirection === sortingDirections.Ascending
                ? this.setState({sortDirection: sortingDirections.Descending})
                : this.setState({sortDirection: sortingDirections.Ascending});
        } else {
            // sorting on a different column than before
            // If a defaultSortDirection isn't specified, sort ascending first
            const defaultSortDirection = this.props.defaultSortDirection;

            const newSortDirection =
                defaultSortDirection ?? sortingDirections.Ascending;
            this.setState({sortDirection: newSortDirection});
        }

        this.setState({sortColumnKey: clickedColumn});
        this.moveToFirstPage();
    }

    render(): JSX.Element {
        const {
            data,
            tableFooterData,
            itemsPerPage,
            customColumns,
            configurableColumns,
            message,
            hideNoResultsMessage,
            exportFileName,
            screenWidth
        } = this.props;

        const staticColumns = this.props.staticColumns;

        // columns which are active are either not affected by toggles or are in an active toggle state
        let activeStaticColumns = staticColumns?.filter((col) => {
            return !col.isToggleable || col.isToggledToVisible;
        });

        const updateActiveStaticColumns = () => {
            activeStaticColumns = staticColumns?.filter((col) => {
                return col.isToggleable === undefined || col.isToggledToVisible;
            });
        };

        const handleToggle = () => {
            this.setState({
                isToggleChecked: !this.state.isToggleChecked
            });

            staticColumns?.forEach((col) => {
                if (col.isToggleable !== undefined) {
                    col.isToggledToVisible = !col.isToggledToVisible;
                }
            });
            updateActiveStaticColumns();
        };

        // If filters don't come from the parent component then have the DataTable handle filters itself
        const filters =
            this.props.filters === undefined
                ? this.state.filters
                : this.props.filters;
        const setFilters =
            this.props.filters === undefined
                ? this.state.setFilters
                : this.props.setFilters;
        const removeAllFilters =
            this.props.filters === undefined
                ? this.state.removeAllFilters
                : this.props.removeAllFilters;

        const isMobileScreen = IsMobileScreen(screenWidth);

        let finalData: IDataTableData[] = data.map((row, index) => ({
            ...row,
            originalIndex: index
        }));

        if (filters && filters.size > 0) {
            filters.forEach((value, key) => {
                if (
                    typeof value.values().next().value === "string" ||
                    // if filter is for column of DataType.None, it is treated as a string
                    // if the value is null or undefined treat it as a 'string' filter
                    value.has(null) ||
                    value.has(undefined)
                ) {
                    //determine if we have string filter or None filter by looking at the first value in the Set for this filter
                    finalData = finalData.filter((dataRow: IDataTableData) => {
                        return value.has(dataRow[key]?.toString());
                    });
                } else if (typeof value.values().next().value === "boolean") {
                    finalData = finalData.filter((dataRow: IDataTableData) => {
                        return value.has(dataRow[key]);
                    });
                }
                //only other option at this point is that it is an INumericalFilter
                // works for Number, Percent, Money, TimeOfDay, TimeSpan, PrettyTimeSpan,
                // works for Time too, with special logic built in to ensure ISO time strings are converted to numbers for comparison
                else {
                    const numericalFilter = value.values().next()
                        .value as INumericalFilter;

                    const isTimeDataType =
                        dataFormatUtil.timeISOStringToTimeOfDayNumber(
                            finalData[0][key]?.toString()
                        ); // Data is for DataType.Time

                    if (numericalFilter.measure === ComparisonMethodType.Equal) {
                        finalData = finalData.filter((dataRow: IDataTableData) => {
                            return dataRow[key] === numericalFilter.amount;
                        });
                    } else if (
                        numericalFilter.measure ===
                        ComparisonMethodType.GreaterThanOrEqual
                    ) {
                        finalData = finalData.filter((dataRow: IDataTableData) => {
                            const rowValue = dataRow[key] as number;

                            if (isTimeDataType) {
                                const rowValueFromTimeToTimeOfDay =
                                    dataFormatUtil.timeISOStringToTimeOfDayNumber(
                                        rowValue.toString()
                                    );

                                if (rowValueFromTimeToTimeOfDay) {
                                    return (
                                        rowValueFromTimeToTimeOfDay >=
                                        numericalFilter.amount
                                    );
                                } else {
                                    return false;
                                }
                            } else {
                                return rowValue >= numericalFilter.amount;
                            }
                        });
                    } else if (
                        numericalFilter.measure ===
                        ComparisonMethodType.LessThanOrEqual
                    ) {
                        finalData = finalData.filter((dataRow: IDataTableData) => {
                            const rowValue = dataRow[key] as number;

                            if (isTimeDataType) {
                                const rowValueFromTimeToTimeOfDay =
                                    dataFormatUtil.timeISOStringToTimeOfDayNumber(
                                        rowValue.toString()
                                    );

                                if (rowValueFromTimeToTimeOfDay) {
                                    return (
                                        rowValueFromTimeToTimeOfDay <=
                                        numericalFilter.amount
                                    );
                                } else {
                                    return false;
                                }
                            } else {
                                return rowValue <= numericalFilter.amount;
                            }
                        });
                    } else if (
                        numericalFilter.measure === ComparisonMethodType.NotEqual
                    ) {
                        finalData = finalData.filter((dataRow: IDataTableData) => {
                            const rowValue = dataRow[key] as number;
                            return rowValue !== numericalFilter.amount;
                        });
                    } else if (
                        numericalFilter.measure === ComparisonMethodType.Between
                    ) {
                        finalData = finalData.filter((dataRow: IDataTableData) => {
                            const rowValue = dataRow[key] as number;

                            if (isTimeDataType) {
                                const rowValueFromTimeToTimeOfDay =
                                    dataFormatUtil.timeISOStringToTimeOfDayNumber(
                                        rowValue.toString()
                                    );

                                if (rowValueFromTimeToTimeOfDay) {
                                    return (
                                        rowValueFromTimeToTimeOfDay >=
                                            numericalFilter.amount &&
                                        rowValueFromTimeToTimeOfDay <=
                                            numericalFilter.secondaryAmount!
                                    );
                                } else {
                                    return false;
                                }
                            } else {
                                return (
                                    rowValue >= numericalFilter.amount &&
                                    rowValue <= numericalFilter.secondaryAmount!
                                );
                            }
                        });
                    }
                }
            });
        }

        //Sorting
        finalData.sort(
            objectComparer(
                this.state.sortColumnKey,
                this.state.sortDirection,
                [...staticColumns, ...(configurableColumns || [])],
                customColumns
            )
        );

        // Get column headers and file name for export
        const exportMetricHeaders = configurableColumns || [];

        let headerColumns = activeStaticColumns;
        //If the table has the ability to expand a details row, add a column to the expand button
        if (this.props.renderDetailBody) {
            const expandColumn: IColumnMetadata = {
                key: "expand",
                label: "",
                dataType: DataType.None,
                decimalPlaces: 0,
                hasFilter: false,
                isToggleable: false,
                isToggledToVisible: true,
                tooltipText: "",
                unit: ""
            };

            headerColumns = [expandColumn, ...activeStaticColumns];
        }

        return (
            <>
                <Grid>
                    {this.props.hasTableToggle && (
                        <Grid.Row columns={2}>
                            <Grid.Column
                                mobile={16}
                                tablet={16}
                                computer={16}
                                textAlign="right"
                            ></Grid.Column>
                            <Grid.Column textAlign="right">
                                <Popup
                                    trigger={
                                        <Checkbox
                                            className={"colored-toggle"}
                                            style={{
                                                verticalAlign: "middle",
                                                marginRight: "10px"
                                            }}
                                            checked={this.state.isToggleChecked}
                                            toggle
                                            onChange={() => handleToggle()}
                                        />
                                    }
                                    content={"Toggle metrics"}
                                    position="top center"
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {filters !== undefined &&
                        filters.size > 0 &&
                        removeAllFilters !== undefined && (
                            <Grid.Row columns={1}>
                                <Grid.Column>
                                    <Button
                                        icon
                                        color="red"
                                        labelPosition="right"
                                        onClick={removeAllFilters}
                                    >
                                        Remove All Filters
                                        <Icon name="delete" />
                                    </Button>
                                </Grid.Column>
                            </Grid.Row>
                        )}
                    <Grid.Row className="padding-row-14px">
                        <div className="table-wrapper">
                            <div className={"custom-date-range-label"}>
                                {message}
                            </div>
                            {/* Only display export when exportFileName is defined. This will ensure that the export button is displayed only for the right modules. */}
                            {exportFileName && (
                                <ExportExcel
                                    data={finalData}
                                    columns={staticColumns.concat(
                                        exportMetricHeaders
                                    )}
                                    fileName={exportFileName}
                                />
                            )}
                            <Divider hidden />
                            {/* clear: "both" needed to clear floated content above */}
                            <div
                                className="overflow-x-scroll"
                                style={{clear: "both"}}
                            >
                                <Table
                                    sortable
                                    basic
                                    striped
                                    stackable={isMobileScreen}
                                    structured
                                    textAlign="center"
                                >
                                    <DataTableHeader
                                        data={data} //original data needed for column filtering functionality
                                        staticColumns={headerColumns}
                                        configurableColumns={configurableColumns}
                                        filters={filters}
                                        setFilters={setFilters}
                                        // Note: sortColumnKey can be initially an array. In that case, display sorting "arrow" on the first column
                                        sortColumnKey={
                                            isArray(this.state.sortColumnKey)
                                                ? this.state.sortColumnKey[0]
                                                : this.state.sortColumnKey
                                        }
                                        sortDirection={this.state.sortDirection}
                                        handleSort={this.handleSort}
                                    />

                                    {data.length && data.length > 0 ? (
                                        <>
                                            <DataTableBody
                                                data={finalData}
                                                staticColumns={activeStaticColumns}
                                                configurableColumns={
                                                    configurableColumns
                                                }
                                                customColumns={customColumns}
                                                currentIndex={
                                                    this.state.currentIndex
                                                }
                                                itemsPerPage={itemsPerPage}
                                                isMobileScreen={isMobileScreen}
                                                renderDetailBody={
                                                    this.props.renderDetailBody
                                                }
                                            />
                                            {tableFooterData ? (
                                                <DataTableFooter
                                                    staticColumns={
                                                        activeStaticColumns
                                                    }
                                                    configurableColumns={
                                                        configurableColumns
                                                    }
                                                    footerData={tableFooterData}
                                                    isMobileScreen={isMobileScreen}
                                                ></DataTableFooter>
                                            ) : null}
                                        </>
                                    ) : hideNoResultsMessage ? null : (
                                        <NoResultsTableBody
                                            numberOfColumns={
                                                activeStaticColumns.length +
                                                (configurableColumns
                                                    ? configurableColumns?.length
                                                    : 0)
                                            }
                                            marginTop={0}
                                        />
                                    )}
                                </Table>
                            </div>
                        </div>
                    </Grid.Row>
                    <Grid.Row columns={1}>
                        <Grid.Column textAlign="right">
                            <PaginationComponent
                                activePage={this.state.activePage}
                                changePage={this.handlePageChange}
                                totalPages={Math.ceil(
                                    finalData.length / itemsPerPage
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </>
        );
    }
}
